import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";
import { listWhsales, deleteWhsale, isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import {
  normalizeListWhsalesRows,
  isWhsalesApiSuccess,
  ERP_WHSALES_LIST_INVALIDATE_EVENT,
} from "../utils/whsalesErpAdapter";
import { parseApiPagination } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import { Modal, Button } from "react-bootstrap";
import { formatDateToDDMMYYYY } from "./const";

// CSS styles for row colors - matching Invoice table styling
const rowStyles = `
  /* Row background by payment status (low opacity) */
  .status-paid-row,
  .status-paid-row td {
    background-color: rgba(25, 135, 84, 0.02) !important; /* green */
  }
  .status-pending-row,
  .status-pending-row td {
    background-color: rgba(220, 53, 69, 0.03) !important; /* red */
  }
  .status-partial-row,
  .status-partial-row td {
    background-color: rgba(255, 193, 7, 0.02) !important; /* amber */
  }
  
  /* Compact table spacing specifically matching Invoice table */
  .invoice-table th,
  .invoice-table td {
    padding: 0.5rem 0.6rem !important; /* reduce vertical and horizontal gaps */
  }
  .invoice-table .badge {
    padding: 0.3em 0.5em !important;
    font-size: 0.75rem !important;
  }
  .invoice-table .btn.btn-sm {
    padding: 0.25rem 0.4rem !important;
  }
`;

if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("whsale-row-styles");
  if (existingStyle) {
    existingStyle.remove();
  }

  const styleSheet = document.createElement("style");
  styleSheet.id = "whsale-row-styles";
  styleSheet.type = "text/css";
  styleSheet.innerText = rowStyles;
  document.head.appendChild(styleSheet);
}

function WholesaleInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const pageCache = useVisitedPageCache();

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [canAdd, setCanAdd] = useState(() =>
    hasPermission(PERMISSIONS.WHSALE_ADD),
  );
  const [canEdit, setCanEdit] = useState(() =>
    hasPermission(PERMISSIONS.WHSALE_EDIT),
  );
  const [canDelete, setCanDelete] = useState(() =>
    hasPermission(PERMISSIONS.WHSALE_DELETE),
  );
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const sync = () => {
      setCanAdd(hasPermission(PERMISSIONS.WHSALE_ADD));
      setCanEdit(hasPermission(PERMISSIONS.WHSALE_EDIT));
      setCanDelete(hasPermission(PERMISSIONS.WHSALE_DELETE));
    };
    window.addEventListener("permissionsUpdated", sync);
    window.addEventListener("erpAuthUpdated", sync);
    return () => {
      window.removeEventListener("permissionsUpdated", sync);
      window.removeEventListener("erpAuthUpdated", sync);
    };
  }, []);

  const fetchList = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey([page, limit, search]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setRows(cached.items);
        setTotalCount(cached.total);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await listWhsales({ search, page, limit });
        if (!isWhsalesApiSuccess(res) && !isErpApiSuccess(res)) {
          setRows([]);
          setTotalCount(0);
          return;
        }
        const list = normalizeListWhsalesRows(res);
        const total = parseApiPagination(res, list.length).total;
        setRows(list);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: list, total });
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Failed to load wholesale bills");
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (location.pathname !== "/whinvoice") return;
    fetchList(currentPage, itemsPerPage, searchQuery, true);
  }, [location.pathname]);

  useEffect(() => {
    const t = setTimeout(
      () => fetchList(currentPage, itemsPerPage, searchQuery),
      searchQuery ? 250 : 0,
    );
    return () => clearTimeout(t);
  }, [currentPage, itemsPerPage, searchQuery, fetchList]);

  useEffect(() => {
    const onInvalidate = () =>
      fetchList(currentPage, itemsPerPage, searchQuery, true);
    window.addEventListener(ERP_WHSALES_LIST_INVALIDATE_EVENT, onInvalidate);
    return () =>
      window.removeEventListener(ERP_WHSALES_LIST_INVALIDATE_EVENT, onInvalidate);
  }, [currentPage, itemsPerPage, searchQuery, fetchList]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteWhsale({ id: deleteTarget.id });
      if (isWhsalesApiSuccess(res) || isErpApiSuccess(res)) {
        toast.success("Wholesale bill deleted");
        setDeleteTarget(null);
        fetchList(currentPage, itemsPerPage, searchQuery, true);
        } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const remainingOf = (row) =>
    Number(row?.remaining_amount ?? row?.money_left ?? 0) || 0;

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card w-100 position-relative overflow-hidden ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Wh Sales</h4>
            <div className="d-flex gap-2">
              {canAdd && (
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  disabled={!isCurrentFinancialYear}
                  title={
                    !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                  }
                  onClick={() => {
                    if (!isCurrentFinancialYear) return;
                    navigate("/whsales");
                  }}
                >
                  Add Wholesale
                </button>
              )}
            </div>
          </div>

          <div className="card-body p-4">
            <div className="d-flex align-items-center mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle invoice-table ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Doc No</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Metal</th>
                    <th className="text-end">Out gm</th>
                    <th className="text-end">Old In gm</th>
                    <th className="text-end">Paid gm</th>
                    <th className="text-end">Baki gm</th>
                    <th className="text-end">Rate</th>
                    <th className="text-end">Total ₹</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length > 0 ? (
                    rows.map((row) => {
                      const isPaid =
                        remainingOf(row) <= 0 && Number(row.baki_gm || 0) <= 0;
                      const isPending =
                        Number(row.paid_gm || 0) === 0 &&
                        (Number(row.paid_amount || 0) === 0 ||
                          remainingOf(row) === Number(row.grand_total || 0));
                      const rowClassName = isPaid
                        ? "status-paid-row"
                        : isPending
                          ? "status-pending-row"
                          : "status-partial-row";

                      return (
                        <tr key={row.id} className={rowClassName}>
                          <td>{row.doc_number || "-"}</td>
                          <td>
                            {formatDateToDDMMYYYY(row.date) || row.date || "-"}
                          </td>
                          <td>
                            <div>{row.customer_name}</div>
                            {row.customer_mobile ? (
                              <div
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#6c757d",
                                }}
                              >
                                {row.customer_mobile}
                              </div>
                            ) : null}
                          </td>
                          <td>{row.metal_type}</td>
                          <td className="text-end">
                            {Number(row.total_out_gm || 0).toFixed(3)}
                          </td>
                          <td className="text-end">
                            {Number(row.old_metal_in_gm || 0).toFixed(3)}
                          </td>
                          <td className="text-end">
                            {Number(row.paid_gm || 0).toFixed(3)}
                          </td>
                          <td className="text-end">
                            {Number(row.baki_gm || 0).toFixed(3)}
                          </td>
                          <td className="text-end">
                            {Number(row.item_rate || 0).toFixed(2)}
                          </td>
                          <td className="text-end">
                            {formatIndianRupee(row.grand_total)}
                          </td>
                          <td className="text-center">
                            {canEdit && (
                              <button
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                title="Edit"
                                onClick={() =>
                                  navigate("/editwhsale", {
                                    state: { whsaleId: row.id },
                                  })
                                }
                              >
                                <Icon
                                  icon="solar:pen-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                            {canEdit && (
                              <button
                                className={`btn btn-primary btn-sm ms-2 ${buttonClass}`}
                                title={
                                  remainingOf(row) <= 0 &&
                                  Number(row.baki_gm || 0) <= 0
                                    ? "Nothing pending"
                                    : "Settlement"
                                }
                                disabled={
                                  remainingOf(row) <= 0 &&
                                  Number(row.baki_gm || 0) <= 0
                                }
                                onClick={() =>
                                  navigate("/whsale-settlement", {
                                    state: {
                                      whsaleId: row.id,
                                      settlementOnly: true,
                                    },
                                  })
                                }
                              >
                                <Icon
                                  icon="solar:wallet-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className={`btn btn-primary btn-sm ms-2 ${buttonClass}`}
                                title="Delete"
                                onClick={() => setDeleteTarget(row)}
                              >
                                <Icon
                                  icon="solar:trash-bin-trash-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No wholesale bills found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(p) => setCurrentPage(p)}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => !deleting && setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete wholesale?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Permanently delete{" "}
          <strong>{deleteTarget?.doc_number || deleteTarget?.id}</strong>?
        </Modal.Body>
        <Modal.Footer>
                          <Button
            variant="secondary"
            disabled={deleting}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
                          </Button>
          <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WholesaleInvoice;
